import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import {
  ManageTemplatesDataToLoadV,
  SupportedPostType,
  TemplateStatus,
  TemplateWithValidSettings,
  TemplateWithValidSettingsV
} from "../types";
import * as t from "io-ts";
import { GlobalBlocksV } from "@/types/global-blocks";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import NotificationLogger from "@/lib/notifications/notifications";
import { fpJsonStringify } from "@/util/fp-json-stringify";

/**
 * if template.id refers to a template that exists, it will be updated
 * if not, it will be created
 *
 * the template as it exists on the server after saving will be returned by this function
 */
export async function saveTemplateToWordPress(
  template: TemplateWithValidSettings
) {
  return await makeWordPressAjaxRequest({
    action: "breakdance_manage_templates_save",
    dataToPost: {
      id: template.id,
      title: template.title,
      postType: template.postType,
      settings: fpJsonStringify(template.settings)
    },
    decoder: TemplateWithValidSettingsV,
    resultKey: "data",
    friendlyName: "Save template"
  });
}

export async function duplicateTemplateToWordPress(
  template: TemplateWithValidSettings
) {
  return await makeWordPressAjaxRequest({
    action: "breakdance_manage_templates_duplicate",
    dataToPost: {
      originalId: template.id,
      title: template.title,
      postType: template.postType,
      settings: fpJsonStringify(template.settings)
    },
    decoder: TemplateWithValidSettingsV,
    resultKey: "data",
    friendlyName: "Duplicate template"
  });
}

export async function getAllTemplatesAndNeededDataFromWordPress(
  postType: SupportedPostType
) {
  return await makeWordPressAjaxRequest({
    action: "breakdance_manage_templates_load_data",
    dataToPost: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      post_type: postType
    },
    decoder: ManageTemplatesDataToLoadV,
    resultKey: "data",
    friendlyName: "Get templates data"
  });
}

export async function getAllGlobalBlocksFromWordPress() {
  return await makeWordPressAjaxRequest({
    action: "breakdance_manage_global_blocks",
    decoder: GlobalBlocksV,
    resultKey: "data",
    friendlyName: "Get global blocks data"
  });
}

export async function deleteTemplateFromWordPress(
  title: string,
  id: number,
  status: TemplateStatus | undefined
) {
  if (
    !confirm(
      `${
        status === "publish" ? "Moving to trash" : "Permanently deleting"
      } "${title}". Are you sure?`
    )
  )
    return undefined;

  return pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_manage_templates_delete",
      dataToPost: {
        id,
        trashOrDelete: status === "trash" ? "delete" : "trash"
      },
      decoder: t.literal("success"),
      resultKey: "data",
      friendlyName: `Deleting "${title}"`
    }),
    fold(
      () => {
        NotificationLogger.log.message(`Failed to delete "${title}"`);
        return undefined;
      },
      () => {
        return id;
      }
    )
  );
}

export async function restoreTemplateFromWordPress(title: string, id: number) {
  if (!confirm(`Restoring "${title}". Are you sure?`)) return undefined;

  return pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_manage_templates_restore",
      dataToPost: {
        id
      },
      decoder: t.literal("success"),
      resultKey: "data",
      friendlyName: `Restoring ${title}`
    }),
    fold(
      () => {
        NotificationLogger.log.message(`Failed to restore "${title}"`);
        return undefined;
      },
      () => {
        return id;
      }
    )
  );
}

export async function emptyTrashForPostType(postType: SupportedPostType) {
  if (!confirm("Delete all trash permanently?")) return false;

  return pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_manage_templates_empty_trash",
      dataToPost: {
        postType: postType
      },
      decoder: t.literal("success"),
      resultKey: "data",
      friendlyName: `Emptying trash for ${postType}`
    }),
    fold(
      () => {
        return false;
      },
      () => {
        return true;
      }
    )
  );
}
